import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import RotatingPhoto from "@/components/RotatingPhoto";
import Marquee from "@/components/Marquee";
import { MaskedLine, Reveal, SectionTag } from "@/components/Reveal";
import { ABOUT, PROFILE, VALUES, RANDOM_FACTS, CHAPTERS } from "@/data";

const About = () => (
  <motion.main
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -24 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className="pt-28"
    data-testid="about-page"
  >
    <header className="px-6 md:px-12">
      <SectionTag no="02" label="ABOUT" />
      <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl md:text-8xl" data-testid="about-title">
        <MaskedLine delay={0.1}><span>THE STORY</span></MaskedLine>
        <MaskedLine delay={0.25}><span className="text-burgundy">BEHIND KIN</span></MaskedLine>
      </h1>
    </header>

    <section className="grid gap-12 px-6 py-20 md:grid-cols-12 md:px-12 md:py-28" data-testid="about-story">
      <div className="md:col-span-5">
        <div className="md:sticky md:top-28">
          <RotatingPhoto
            images={ABOUT.portraitPool}
            ratio="aspect-[3/4]"
            label="KIN HAKAN — THROUGH THE JOURNEY"
            className="border border-ink/15 shadow-[-14px_14px_0_0_#0F1115]"
          />
          <p className="mt-4 font-serif text-lg italic text-ash">{PROFILE.location}</p>
        </div>
      </div>
      <div className="md:col-span-7">
        {ABOUT.blocks.map((b, i) =>
          b.big ? (
            <MaskedLine key={i} delay={0.1}>
              <span className="my-10 block font-display text-4xl font-black uppercase tracking-tighter text-burgundy md:text-6xl">
                {b.big}
              </span>
            </MaskedLine>
          ) : (
            <Reveal key={i} delay={i * 0.05}>
              <p className={`mb-8 max-w-xl leading-relaxed md:text-lg ${b.italic ? "font-serif text-2xl italic text-ash md:text-3xl" : "text-ink/80"}`}>
                {b.text}
              </p>
            </Reveal>
          )
        )}
        <Reveal>
          <blockquote className="mt-12 border-l-2 border-burgundy pl-6 font-serif text-2xl italic leading-relaxed md:text-3xl" data-testid="about-closing-quote">
            "{ABOUT.closing}"
          </blockquote>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-10 font-display text-2xl font-black uppercase tracking-tight md:text-3xl" data-testid="about-final-line">
            {ABOUT.finalLine}
          </p>
        </Reveal>
      </div>
    </section>

    <Marquee items={CHAPTERS.map((c) => c.title)} />

    <section className="bg-ink px-6 py-28 text-paper md:px-12" data-testid="values-section">
      <SectionTag no="—" label="MY OPERATING SYSTEM" dark />
      <p className="mt-6 max-w-lg font-serif text-xl italic text-paper/50">
        Bukan sekadar "values" — ini sistem yang menjalankan semuanya.
      </p>
      <div className="mt-14 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {VALUES.map((v, i) => (
          <Reveal key={v.key} delay={i * 0.05} className="bg-ink">
            <div className="group p-8 transition-colors duration-300 hover:bg-coal md:p-10" data-testid={`value-${v.key.toLowerCase()}`}>
              <span className="font-display text-xs text-ember">0{i + 1}</span>
              <h3 className="mt-4 font-display text-2xl font-black uppercase tracking-tight md:text-3xl">{v.key}</h3>
              <p className="mt-3 font-serif text-lg italic text-paper/60">{v.line}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="px-6 py-24 md:px-12 md:py-32" data-testid="random-facts-section">
      <SectionTag no="+" label="A FEW THINGS ABOUT ME" />
      <div className="mt-12 max-w-3xl">
        {RANDOM_FACTS.map((f, i) => (
          <Reveal key={i} delay={i * 0.04}>
            <p
              className="border-b border-ink/10 py-6 font-display text-xl font-bold tracking-tight transition-colors duration-300 hover:text-burgundy md:text-2xl"
              data-testid={`fact-${i}`}
            >
              <span className="mr-4 font-serif text-base italic text-burgundy">{String(i + 1).padStart(2, "0")}</span>
              "{f}"
            </p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <Link
          to="/experiences"
          data-testid="about-to-experiences"
          data-cursor="GO"
          className="group mt-14 inline-flex items-center gap-3 border border-ink px-8 py-4 font-display text-xs font-bold uppercase tracking-[0.3em] transition-colors duration-300 hover:bg-ink hover:text-paper"
        >
          See the experiences
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </Reveal>
    </section>
  </motion.main>
);

export default About;
